import { toQuizQuestions } from '../../pages/setTextReaderGate.js';
import { isAnswerComplete, isQuestionCorrect, clampCardIndex } from '../practice/practiceHelpers.js';

export const INTRODUCTION_QUIZ_REVIEW_LABELS = Object.freeze({ hook: 'Hook', issue: 'Issue', position: 'Position' });
const DEFAULT_SKILLS = Object.freeze(['hook', 'issue', 'position', 'mixed']);
const SUPPORTED_SKILLS = new Set([...DEFAULT_SKILLS, 'entrance', 'pun', 'allit']);
const allowedQuizSkills = skills => new Set(Array.isArray(skills) ? skills.filter(skill => SUPPORTED_SKILLS.has(skill)) : []);

// Items must already come from the entitled reader list. This second boundary
// only admits this actual lesson's native quizzes; a URL never supplies content.
export function introductionQuizQuestions({ entitled, lessonId, items, skillsByQuizId = {}, allowedSkills = DEFAULT_SKILLS } = {}) {
    if (!entitled || typeof lessonId !== 'string' || !lessonId || !Array.isArray(items)) return [];
    const skills = allowedQuizSkills(allowedSkills);
    const ids = new Set();
    const native = items.filter(item => {
        if (item?.type !== 'quiz' || typeof item.id !== 'string' || !item.id || ids.has(item.id)
            || item.content?.relatedTextId !== lessonId) return false;
        ids.add(item.id);
        return true;
    });
    const originals = new Map(native.map(item => [item.id, item]));
    return toQuizQuestions(native).map(question => {
        const raw = originals.get(question.id);
        const mapped = skillsByQuizId?.[question.id];
        const skill = mapped === 'all' ? 'mixed' : mapped;
        return { ...question, hint: raw.content.hints?.[0] || null,
            skill: skills.has(skill) ? skill : null };
    });
}

// Review links only use named lesson skills explicitly enabled by the caller.
// The original bank keeps its Hook/Issue/Position defaults; a supplement opts in.
export function introductionQuizReviewSkills(question, { reviewLabels = INTRODUCTION_QUIZ_REVIEW_LABELS, allowedSkills = DEFAULT_SKILLS } = {}) {
    const skills = allowedQuizSkills(allowedSkills);
    if (!skills.has(question?.skill)) return [];
    const labels = reviewLabels && typeof reviewLabels === 'object' ? reviewLabels : {};
    const labelled = Object.keys(labels).filter(skill => skill !== 'mixed' && skills.has(skill)
        && typeof labels[skill] === 'string' && labels[skill].trim());
    return question?.skill === 'mixed' ? labelled : labelled.filter(skill => skill === question?.skill);
}

export function introductionQuizSummary(questions, state = {}) {
    let checked = 0, correct = 0, completedResponses = 0, correctResponses = 0;
    const totalResponses = questions.reduce((count, q) => count + (q.type === 'matching' ? q.pairs.length : 1), 0);
    for (const q of questions) {
        if (!state.checked?.[q.id] || !isAnswerComplete(q, state.answers?.[q.id])) continue;
        checked += 1;
        const answer = state.answers[q.id];
        if (isQuestionCorrect(q, answer)) correct += 1;
        const weight = q.type === 'matching' ? q.pairs.length : 1;
        completedResponses += weight;
        correctResponses += q.type === 'matching' ? q.pairs.filter((_, i) => answer[i] === i).length : (isQuestionCorrect(q, answer) ? 1 : 0);
    }
    return { questions: questions.length, totalResponses, checked, correct, completedResponses, correctResponses };
}

// Deliberately page-memory only. The reader owns the state and clears it on an
// owner/pack change, while changing route/lesson cards does not discard it.
export function updateIntroductionQuiz(questions, state = {}, action = {}) {
    const current = questions.find(q => q.id === action.id);
    const answers = { ...(state.answers || {}) }, checked = { ...(state.checked || {}) };
    const index = clampCardIndex(state.index, questions.length);
    if (action.type === 'select') return { answers, checked, index: clampCardIndex(action.index, questions.length) };
    if (action.type === 'reset') return { answers: {}, checked: {}, index: 0 };
    if (!current) return state;
    if (action.type === 'answer' && !checked[current.id]) answers[current.id] = action.value;
    if (action.type === 'check' && isAnswerComplete(current, answers[current.id])) checked[current.id] = true;
    if (action.type === 'retry') delete checked[current.id];
    return { answers, checked, index };
}
